import { ReactNode, RefObject } from 'react';
import { ScrollView, View, Pressable, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { effectiveTopInset } from '@/utils/effectiveTopInset';
import { useSegments } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Title } from '../ui/Text';
import { OfflineIndicator } from '../ui/OfflineIndicator';

type ScreenProps = {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  scrollable?: boolean;
  /** When scrollable, attach to the main ScrollView (e.g. scroll-to-section from the screen). */
  scrollViewRef?: RefObject<ScrollView | null>;
  headerAction?: ReactNode;
  floating?: ReactNode;
  showOfflineIndicator?: boolean;
  disableTopInset?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  /** Outer container style (root flex wrapper). */
  style?: StyleProp<ViewStyle>;
  /** Merged into scroll/content container (e.g. transparent hero backgrounds). */
  contentStyle?: StyleProp<ViewStyle>;
  /** When scrollable, merged after `contentStyle` on ScrollView `contentContainerStyle`. */
  contentContainerStyle?: StyleProp<ViewStyle>;
  showsVerticalScrollIndicator?: boolean;
};

export function Screen({
  title,
  subtitle,
  children,
  scrollable = true,
  scrollViewRef,
  headerAction,
  floating,
  showOfflineIndicator = true,
  disableTopInset = false,
  showBackButton = false,
  onBack,
  contentStyle,
  contentContainerStyle,
  style,
  showsVerticalScrollIndicator = false,
}: ScreenProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const segments = useSegments();
  const inTabs = segments?.[0] === '(tabs)';
  /** Android edge-to-edge sometimes reports `insets.top === 0` before window insets apply; blend with StatusBar height. */
  const topInset = effectiveTopInset(insets.top);

  // On tab screens, the tab bar already accounts for safe-area bottom padding.
  // Using insets.bottom here creates a visible blank strip above the tab bar during normal scroll.
  const bottomPadding = inTabs ? theme.spacing(2) : theme.spacing(2) + insets.bottom;

  // Tabs use a custom height: 56 + safe-area padding (min 8). Keep floating UI above it.
  const tabBarHeight = inTabs ? 56 + Math.max(insets.bottom, 8) : 0;
  const floatingBottomOffset = tabBarHeight + theme.spacing(2);

  const content = scrollable ? (
    <ScrollView
      ref={scrollViewRef}
      style={[{ flex: 1, backgroundColor: theme.colors.background }, contentStyle]}
      contentContainerStyle={[
        {
          backgroundColor: theme.colors.background,
          paddingHorizontal: theme.spacing(2),
          paddingBottom: bottomPadding,
          gap: theme.spacing(2),
        },
        contentStyle,
        contentContainerStyle,
      ]}
      showsVerticalScrollIndicator={showsVerticalScrollIndicator}
      keyboardShouldPersistTaps="always"
      keyboardDismissMode="on-drag"
      nestedScrollEnabled={true}
    >
      {children}
    </ScrollView>
  ) : (
    <View
      style={[
        {
          flex: 1,
          backgroundColor: theme.colors.background,
          paddingHorizontal: theme.spacing(2),
          paddingBottom: bottomPadding,
          gap: theme.spacing(2),
        },
        contentStyle,
      ]}
    >
      {children}
    </View>
  );

  return (
    <View
      style={[
        {
          flex: 1,
          backgroundColor: theme.colors.background,
          paddingTop: disableTopInset ? 0 : topInset + theme.spacing(0.5),
        },
        style,
      ]}
    >
      {/* Back Button - Top Left */}
      {showBackButton && onBack && (
        <View
          style={{
            paddingHorizontal: theme.spacing(2),
            marginBottom: theme.spacing(0.5),
          }}
        >
          <Pressable
            onPress={onBack}
            style={({ pressed }) => ({
              alignSelf: 'flex-start',
              padding: theme.spacing(0.5),
              borderRadius: theme.radii.md,
              opacity: pressed ? 0.7 : 1,
            })}
            accessibilityLabel="Go back"
            accessibilityRole="button"
          >
            <Ionicons name="chevron-back" size={24} color={theme.colors.text} />
          </Pressable>
        </View>
      )}

      {/* Title/Subtitle and Header Action */}
      {(title || subtitle || headerAction) && (
        <View
          style={{
            paddingHorizontal: theme.spacing(2),
            marginBottom: theme.spacing(2),
            flexDirection: 'row',
            alignItems: 'flex-start',
          }}
        >
          <View style={{ flex: 1, gap: theme.spacing(0.5), minWidth: 0 }}>
            {title && (
              <Title
                style={{
                  color: theme.colors.text,
                  fontSize: 24,
                  lineHeight: 30,
                }}
              >
                {title}
              </Title>
            )}
            {subtitle && (
              <ThemedText variant="body" color={theme.colors.textSecondary}>
                {subtitle}
              </ThemedText>
            )}
          </View>
          {headerAction}
        </View>
      )}
      {showOfflineIndicator && (
        <View style={{ paddingHorizontal: theme.spacing(2) }}>
          <OfflineIndicator
            variant="banner"
            enableSyncModal
            enableConflictModal
          />
        </View>
      )}
      {content}
      {floating ? (
        <View pointerEvents="box-none" style={StyleSheet.absoluteFillObject}>
          <View
            pointerEvents="box-none"
            style={{
              position: 'absolute',
              right: theme.spacing(2),
              bottom: floatingBottomOffset,
              alignItems: 'flex-end',
            }}
          >
            {floating}
          </View>
        </View>
      ) : null}
    </View>
  );
}

